/**
 * Native-shell bridge. All Capacitor APIs are imported lazily so this file
 * is safe to load in the web build — `Capacitor.isNativePlatform()` is the
 * single gate.
 *
 * Currently wires:
 *   - Push notifications (registers the device token with the server via
 *     `tRPC push.registerToken`)
 *
 * Add other native integrations (status bar, splash hide, deep-link
 * handler) here so the entry point in `main.tsx` stays a one-liner.
 */

import type { TRPCClient } from "@trpc/client";
import type { AppRouter } from "../../server/routers";

let initialized = false;

/**
 * Custom URL scheme used for OAuth deep links. Must match
 * `ALLOWED_NATIVE_SCHEMES` in `server/_core/googleAuth.ts` and the iOS
 * URL scheme registered in `ios/App/App/Info.plist` /
 * Android intent-filter in `android/app/src/main/AndroidManifest.xml`.
 */
export const NATIVE_SCHEME = "com.receipt.app://";
export const NATIVE_AUTH_RETURN = `${NATIVE_SCHEME}auth/complete`;

/**
 * Native Google sign-in via the OS-native account picker
 * (`@capgo/capacitor-social-login`). This is the Google-APPROVED native
 * flow: no in-app webview (Google blocks OAuth in embedded webviews), no
 * external-browser bounce, no custom-scheme deep link. The plugin returns
 * a signed Google ID token; we POST it to `/api/auth/google/native`, which
 * verifies it against Google's JWKS and sets our session cookie via a
 * Set-Cookie header (reliable in the native HTTP cookie store).
 *
 * Client IDs are public (the web client ID already appears in the OAuth
 * redirect URL), so baking them in is fine. The iOS client ID is required
 * for the iOS GoogleSignIn SDK; Android uses the web client ID as its
 * serverClientId. Both can be overridden at build time via Vite env.
 */
// Native OAuth clients all live in the same Google Cloud project as the
// existing web sign-in client (project number 276517720582, the "MOM"
// project). Keeping them together means the server's existing
// GOOGLE_CLIENT_ID already matches the Android ID-token audience, so no
// server reconfiguration of the web client is needed.
//
// - webClientId: the existing "Web application" OAuth client. Used as the
//   serverClientId for Android Credential Manager, so Android ID tokens
//   carry THIS as their audience (which the server already accepts).
// - iOSClientId: the iOS OAuth client (bundle com.mohammadtaha.receipt).
//   iOS ID tokens carry this as their audience; the server accepts it via
//   GOOGLE_IOS_CLIENT_ID. Public values; overridable via Vite env.
const GOOGLE_WEB_CLIENT_ID =
  ((import.meta as any).env?.VITE_GOOGLE_WEB_CLIENT_ID as string | undefined) ||
  "276517720582-8mb3tsmpcuoitdivacj3c58a8mkeoi5d.apps.googleusercontent.com";
const GOOGLE_IOS_CLIENT_ID =
  ((import.meta as any).env?.VITE_GOOGLE_IOS_CLIENT_ID as string | undefined) ||
  "276517720582-kqv2cj67p2c65edj6h0buhlr9vqfjmqb.apps.googleusercontent.com";

let socialLoginInited = false;

async function ensureSocialLoginInit() {
  if (socialLoginInited) return;
  const { SocialLogin } = await import("@capgo/capacitor-social-login");
  await SocialLogin.initialize({
    google: {
      webClientId: GOOGLE_WEB_CLIENT_ID,
      iOSClientId: GOOGLE_IOS_CLIENT_ID,
      mode: "online",
    },
  });
  socialLoginInited = true;
}

/**
 * Trigger the native Google account picker, exchange the returned ID token
 * for a session cookie, and reload into the authenticated app. Throws on
 * cancel/failure so the caller can surface an error toast.
 */
export async function nativeGoogleSignIn(): Promise<void> {
  const { SocialLogin } = await import("@capgo/capacitor-social-login");
  await ensureSocialLoginInit();

  const res = await SocialLogin.login({
    provider: "google",
    options: { scopes: ["email", "profile"] },
  });
  const idToken =
    res?.result && "idToken" in res.result
      ? (res.result as { idToken: string | null }).idToken
      : null;
  if (!idToken) {
    throw new Error("Google sign-in did not return an ID token");
  }

  const resp = await fetch("/api/auth/google/native", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ idToken }),
    credentials: "include",
  });
  if (!resp.ok) {
    const detail = await resp.text().catch(() => "");
    throw new Error(`native sign-in failed (${resp.status}): ${detail}`);
  }
  // Cookie is now set in the native HTTP cookie store. RELOAD THE CURRENT
  // DOCUMENT — do not navigate to "/".
  //
  // This used to be `window.location.href = "/"`, which threw away whatever URL
  // the user actually arrived on. That mattered for exactly one flow: tapping a
  // notification. Push delivery is native-only (server/_core/push.ts routes ios
  // and android and returns false for anything else), the tap sets
  // `window.location.href` to the notification's URL (see the
  // `pushNotificationActionPerformed` handler below), and a signed-out TabShell
  // renders `<Landing/>` IN PLACE without navigating — so the deep link is still
  // the document URL right up to this line, where it was being discarded. A user
  // tapping "3 things need your attention" signed in and landed on a plain Home.
  //
  // `reload()` re-requests the CURRENT url, now with the session cookie set.
  // Nothing is captured, stored, serialised, validated or re-emitted: the intent
  // is not carried, it is simply not destroyed. That is the whole point of this
  // shape — it introduces no new attacker-influenceable input, no returnTo
  // parameter, no redirect validator and no auth-server change. `/?insights=1`
  // then reaches the existing NudgeInbox effect, which opens the inbox once and
  // strips only that parameter.
  //
  // Both failure paths above `throw`, so a failed sign-in never reaches here.
  window.location.reload();
}

export async function initNativeBridge(trpcClient: TRPCClient<AppRouter>) {
  if (initialized) return;
  initialized = true;

  const { Capacitor } = await import("@capacitor/core");
  if (!Capacitor.isNativePlatform()) return;

  // Register the deep-link handler FIRST and on its own, before the
  // other integrations. `registerPushNotifications` awaits a native
  // permission dialog; even though Promise.all runs concurrently, we
  // want the `appUrlOpen` listener attached as early as possible so a
  // fast OAuth-return event can't arrive before the listener exists.
  // It also immediately drains any cold-start launch URL.
  await registerDeepLinkHandler();

  // The rest are independent; run in parallel. Any single failure is
  // swallowed in its own try/catch so e.g. a push permission denial
  // doesn't block splash hide or status-bar styling.
  await Promise.all([
    registerPushNotifications(trpcClient),
    configureStatusBar(),
    hideSplashWhenReady(),
    configureKeyboard(),
  ]);
}

/** True if we're running inside the Capacitor native shell. */
export async function isNative(): Promise<boolean> {
  try {
    const { Capacitor } = await import("@capacitor/core");
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

async function registerPushNotifications(trpcClient: TRPCClient<AppRouter>) {
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");

    // Ask for permission. iOS shows the system prompt; Android grants
    // automatically except on API 33+ which requires POST_NOTIFICATIONS.
    const perm = await PushNotifications.requestPermissions();
    if (perm.receive !== "granted") return;

    await PushNotifications.register();

    PushNotifications.addListener("registration", async tokenEvent => {
      const platform = await detectPlatform();
      try {
        await trpcClient.push.registerToken.mutate({
          platform,
          token: tokenEvent.value,
        });
      } catch (err) {
        console.warn("[native/push] registerToken failed", err);
      }
    });

    PushNotifications.addListener("registrationError", err => {
      console.warn("[native/push] registration error", err);
    });

    // Tap-through: when the user taps the OS notification, route to the
    // attached URL if the server included one.
    PushNotifications.addListener(
      "pushNotificationActionPerformed",
      action => {
        const url = (action.notification.data as { url?: string } | undefined)
          ?.url;
        if (url) window.location.href = url;
      },
    );
  } catch (err) {
    console.warn("[native/push] init failed", err);
  }
}

/**
 * Wire `App.appUrlOpen` so OAuth completions and shared invite links
 * routed via the custom scheme land back in the running app.
 *
 * Two cases handled today:
 *   1. `com.receipt.app://auth/complete?token=<jwt>` — set the JWT as a
 *      cookie inside the WebView jar, then bounce to `/` so the next
 *      request authenticates and the year-long cookie is issued.
 *   2. `com.receipt.app://invite/:token` — route to the existing
 *      `/invite/:token` web flow without leaving the WebView.
 */
/**
 * Process a single custom-scheme deep link. Pure routing — safe to
 * call from BOTH the live `appUrlOpen` event AND the cold-start
 * `getLaunchUrl()` path (see below). Returns true if it handled the
 * URL (so the caller knows a navigation was triggered).
 */
/**
 * Robustly pull the `token` query param out of a custom-scheme deep
 * link. iOS sometimes appends a trailing fragment (`#`) to the URL it
 * hands the app; a naive `URLSearchParams` then sweeps that `#…` INTO
 * the token value, corrupting the JWT → the server rejects it as
 * "invalid" (401). We strip any fragment BEFORE parsing.
 */
function extractDeepLinkToken(tail: string): string | null {
  const noFragment = tail.split("#")[0]; // drop trailing #… that iOS adds
  const qi = noFragment.indexOf("?");
  if (qi < 0) return null;
  const token = new URLSearchParams(noFragment.slice(qi + 1)).get("token");
  return token && token.trim() ? token.trim() : null;
}

function handleDeepLink(url: string): boolean {
  console.info("[native/deeplink] received:", url);
  if (!url.startsWith(NATIVE_SCHEME)) return false;
  const tail = url.slice(NATIVE_SCHEME.length); // e.g. "auth/complete?token=..."

  // OAuth completion. Hand the token to the server's
  // `/api/auth/native-complete` endpoint, which validates it and sets
  // the real session cookie via an HTTP Set-Cookie header, then 302s
  // to "/". We deliberately do NOT set `document.cookie` here: on iOS
  // WKWebView, JS-set cookies are not reliably flushed to the HTTP
  // cookie store that fetch/XHR use, so the next /api/trpc call
  // carried no session and the user bounced back to login. A server
  // Set-Cookie lands in the WKWebView HTTP cookie store correctly.
  if (tail.startsWith("auth/complete")) {
    const token = extractDeepLinkToken(tail);
    console.info(
      "[native/deeplink] auth/complete token length:",
      token ? token.length : 0,
    );
    if (!token) {
      window.location.href = "/";
      return true;
    }
    // NATIVE-AUTH-001: this used to be a top-level GET navigation carrying the
    // token in the query string. A GET that mints a session cookie can be
    // triggered cross-site (an <img> tag is enough), which let an attacker
    // plant THEIR session in a victim's browser. The exchange is now a POST.
    // The Set-Cookie on the POST response lands in the WKWebView HTTP cookie
    // store exactly as the navigation did — the same mechanism the native
    // Google sign-in POST above already relies on. We navigate only after the
    // cookie is set.
    void (async () => {
      try {
        const resp = await fetch("/api/auth/native-complete", {
          method: "POST",
          headers: { "content-type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ token }),
        });
        if (!resp.ok) {
          console.warn("[native/deeplink] auth/complete failed:", resp.status);
        }
      } catch (err) {
        console.warn("[native/deeplink] auth/complete request failed", err);
      } finally {
        // Land on the app either way: on success the session cookie is set, on
        // failure the app shows the login screen rather than a dead end.
        window.location.href = "/";
      }
    })();
    return true;
  }

  // Invite deep link → route via wouter
  if (tail.startsWith("invite/")) {
    window.location.href = `/${tail}`;
    return true;
  }

  console.warn("[native/deeplink] unhandled scheme", url);
  return false;
}

async function registerDeepLinkHandler() {
  try {
    const { App } = await import("@capacitor/app");

    // Case 1 — app ALREADY running: iOS resumes it and fires this.
    App.addListener("appUrlOpen", event => {
      handleDeepLink(event.url);
    });

    // Case 2 — COLD START: the deep link LAUNCHED the app (it was
    // killed/evicted while the user was in Safari completing Google
    // OAuth — extremely common, since the OAuth round-trip can take
    // long enough for iOS to reclaim the backgrounded app). In that
    // case the URL is delivered to the native layer BEFORE the webview
    // JS attaches the appUrlOpen listener, so the event is missed and
    // the token is silently dropped → user lands on the login screen.
    // `getLaunchUrl()` recovers the URL the app was launched with, so
    // we process it here on startup. This was THE bug behind "Google
    // login returns to the app but it stays on the login page."
    try {
      const launch = await App.getLaunchUrl();
      console.info(
        "[native/deeplink] getLaunchUrl on init:",
        launch?.url ?? "(none)",
      );
      if (launch?.url) handleDeepLink(launch.url);
    } catch (e) {
      console.warn("[native/deeplink] getLaunchUrl failed", e);
    }
  } catch (err) {
    console.warn("[native/deeplink] init failed", err);
  }
}

/**
 * Set the status bar to match the app's cream background. Without
 * this, iOS renders a black-on-white bar (light content style) over
 * Receipt's coral splash which looks unfinished.
 */
async function configureStatusBar() {
  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    await StatusBar.setStyle({ style: Style.Dark }); // dark text on light bg
    await StatusBar.setBackgroundColor({ color: "#FFFAF5" });
  } catch {
    /* status-bar plugin unavailable on web; safe to ignore */
  }
}

/**
 * Splash screen is shown by Capacitor automatically on launch. We hide
 * it after the React tree has mounted + the auth check has finished
 * — that's roughly when the app is genuinely interactive. Capping at
 * 2500ms so a slow API doesn't keep the splash up indefinitely.
 */
async function hideSplashWhenReady() {
  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    // Hide after a short delay + on document ready. Capacitor will
    // also auto-hide via `launchAutoHide` in capacitor.config.ts —
    // this is the belt-and-braces version that fires on real readiness.
    const hide = () => SplashScreen.hide().catch(() => {});
    if (document.readyState === "complete") {
      setTimeout(hide, 400);
    } else {
      window.addEventListener("load", () => setTimeout(hide, 400), {
        once: true,
      });
    }
    setTimeout(hide, 2500); // hard ceiling
  } catch {
    /* splash plugin unavailable on web */
  }
}

/**
 * Keyboard handling — push the viewport up when the keyboard opens
 * so input fields aren't hidden. Capacitor's `Keyboard` plugin emits
 * `keyboardWillShow` / `keyboardWillHide` events we can hook into
 * for finer control later (e.g. lifting the FAB out of the way).
 * For now we just let the OS resize the WebView naturally.
 */
async function configureKeyboard() {
  try {
    const { Keyboard, KeyboardResize } = await import(
      "@capacitor/keyboard"
    );
    // Standard native keyboard handling. (The Capacitor shell is a
    // stopgap that will be replaced by fully-native iOS/Android apps; we
    // keep the OS-default behaviour rather than custom JS keyboard hacks.)
    await Keyboard.setResizeMode({ mode: KeyboardResize.Native });
    await Keyboard.setScroll({ isDisabled: false });
  } catch {
    /* keyboard plugin unavailable on web */
  }
}

async function detectPlatform(): Promise<"ios" | "android" | "web"> {
  const { Capacitor } = await import("@capacitor/core");
  const platform = Capacitor.getPlatform();
  if (platform === "ios") return "ios";
  if (platform === "android") return "android";
  return "web";
}
